import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal, ConfirmDialog } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { usePayerDirectoryEntry } from '../hooks/usePayerDirectoryEntry';
import { usePayerDeleteImpact } from '../hooks/usePayerDeleteImpact';
import { useAllPayers } from '../hooks/useAllPayers';
import { useChartingRuns } from '../hooks/useChartingRuns';
import { useIvrMapVersions } from '../hooks/useIvrMapVersions';
import { PayerDirectoryEditor } from '../components/PayerDirectoryEditor';
import { RunChartingSection } from '../components/RunChartingSection';
import { IvrMapCard } from '../components/IvrMapCard';
import { PayerKnowledgePanel } from '../components/PayerKnowledgePanel';
import { ChartingCallsCard } from '../components/ChartingCallsCard';
import { PayerDiagnosticBanner } from '../components/PayerDiagnosticBanner';
import { statusBadge, strategyBadge } from '../components/payerBadges';
import type { PayerDeleteImpact, DirectoryPayer } from '../api/payersApi';
import styles from './AgencyPayerDetailPage.module.css';

function pluralize(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function deleteImpactMessage(
  payer: DirectoryPayer,
  impact: PayerDeleteImpact | null,
  isLoading: boolean,
): string {
  if (isLoading || !impact) return `Checking what references "${payer.name}"…`;

  const parts = [
    impact.locationCount > 0 ? pluralize(impact.locationCount, 'location') : null,
    impact.chartingRunCount > 0 ? pluralize(impact.chartingRunCount, 'charting run') : null,
    impact.mapVersionCount > 0 ? pluralize(impact.mapVersionCount, 'map version') : null,
  ].filter((p): p is string => !!p);

  if (parts.length === 0) return `Delete "${payer.name}" from the directory? This can't be undone.`;

  return `"${payer.name}" is connected to ${parts.join(', ')}. Deleting it will remove it from all of these too — this can't be undone.`;
}

export function AgencyPayerDetailPage() {
  const { payerId } = useParams<{ payerId: string }>();
  const navigate = useNavigate();
  const { entry } = usePayerDirectoryEntry(payerId ?? null);
  const { bundle } = useIvrMapVersions(payerId ?? null);
  const { runs } = useChartingRuns(payerId ?? null);
  const { deletePayer } = useAllPayers();
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const { impact: deleteImpact, isLoading: deleteImpactLoading } = usePayerDeleteImpact(
    confirmingDelete ? (payerId ?? null) : null,
  );

  if (!payerId) return null;

  const goToDirectory = () => navigate('/agency/insurance-payers');

  return (
    <div className={patterns.page}>
      <span className={styles.back} onClick={goToDirectory}>
        ← All payers
      </span>

      <div className={styles.head}>
        <div>
          <div className={styles.title}>
            {entry?.name ?? 'Payer'}
            {entry && (
              <>
                {statusBadge(entry.status)}
                {strategyBadge(entry.verificationStrategy)}
              </>
            )}
          </div>
          {entry && (
            <div className={styles.meta}>
              {entry.payerType}
              {entry.stateEntity ? ` · ${entry.stateEntity}` : ''} · provider line{' '}
              <b>{entry.providerLine ?? '—'}</b> · {pluralize(runs.length, 'charting run')} ·{' '}
              <span className={styles.metaLink} onClick={() => setEditing(true)}>
                edit payer
              </span>{' '}
              ·{' '}
              <span className={styles.metaLink} onClick={() => setConfirmingDelete(true)}>
                delete payer
              </span>
            </div>
          )}
        </div>
        <div className={styles.actions}>
          <RunChartingSection payerId={payerId} />
        </div>
      </div>

      <PayerDiagnosticBanner payerId={payerId} />

      <div className={styles.card}>
        <IvrMapCard payerId={payerId} />
      </div>

      <div className={styles.card}>
        <PayerKnowledgePanel
          knowledge={bundle?.active?.knowledge ?? null}
          version={bundle?.active?.version ?? null}
        />
      </div>

      <div className={styles.card}>
        <ChartingCallsCard payerId={payerId} />
      </div>

      <Modal isOpen={editing} onClose={() => setEditing(false)} title="Edit payer">
        <PayerDirectoryEditor payerId={payerId} onCancel={() => setEditing(false)} />
      </Modal>

      <ConfirmDialog
        isOpen={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={() => {
          deletePayer.mutate(payerId, { onSuccess: goToDirectory });
        }}
        title="Delete this payer?"
        message={entry ? deleteImpactMessage(entry, deleteImpact, deleteImpactLoading) : ''}
        confirmLabel="Delete"
        confirmDisabled={deleteImpactLoading}
        variant="destructive"
        isLoading={deletePayer.isPending}
      />
    </div>
  );
}
